import React, { useState, useEffect, useCallback } from 'react';
import { MapPin, UtensilsCrossed, Globe, Tag, Plus, Pencil, Trash2, RefreshCw } from 'lucide-react';
import {
  getAdminReferenceCountries,
  createAdminReferenceCountry,
  updateAdminReferenceCountry,
  deleteAdminReferenceCountry,
  getAdminReferenceCities,
  createAdminReferenceCity,
  updateAdminReferenceCity,
  deleteAdminReferenceCity,
  getAdminReferenceCuisineTypes,
  createAdminReferenceCuisineType,
  updateAdminReferenceCuisineType,
  deleteAdminReferenceCuisineType,
  getAdminDishCategories,
  createAdminDishCategory,
  updateAdminDishCategory,
  deleteAdminDishCategory,
} from '../../../services/adminApi';

const TERRACOTTA = 'rgb(239, 76, 35)';
const input =
  'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500/30 focus:border-orange-500';

export default function AdminReferenceData() {
  const [tab, setTab] = useState('countries');
  const [countries, setCountries] = useState([]);
  const [cities, setCities] = useState([]);
  const [cuisines, setCuisines] = useState([]);
  const [dishCategories, setDishCategories] = useState([]);
  const [filterCountryId, setFilterCountryId] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingCountry, setEditingCountry] = useState(null);

  const loadCountries = useCallback(async () => {
    const data = await getAdminReferenceCountries(true);
    setCountries(Array.isArray(data) ? data : []);
  }, []);

  const loadCities = useCallback(async () => {
    const cid = filterCountryId === '' ? null : Number(filterCountryId);
    const data = await getAdminReferenceCities(cid, true);
    setCities(Array.isArray(data) ? data : []);
  }, [filterCountryId]);

  const loadCuisines = useCallback(async () => {
    const data = await getAdminReferenceCuisineTypes(true);
    setCuisines(Array.isArray(data) ? data : []);
  }, []);

  const loadDishCategories = useCallback(async () => {
    const data = await getAdminDishCategories();
    setDishCategories(Array.isArray(data) ? data : []);
  }, []);

  const refresh = async () => {
    setError('');
    setLoading(true);
    try {
      await Promise.all([loadCountries(), loadCities(), loadCuisines(), loadDishCategories()]);
    } catch (e) {
      setError(e.message || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  useEffect(() => {
    if (tab === 'cities') loadCities();
  }, [tab, filterCountryId, loadCities]);

  return (
    <div className="max-w-6xl mx-auto py-6 pl-3 pr-4 sm:pl-4 sm:pr-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Globe size={28} style={{ color: TERRACOTTA }} />
            Référence géographique & cuisine
          </h1>
          <p className="text-gray-600 text-sm mt-1">
            Pays, villes et types de cuisine affichés sur le formulaire d&apos;inscription restaurateur.
          </p>
        </div>
        <button type="button" onClick={refresh} className="flex items-center gap-2 px-3 py-2 border rounded-lg text-sm hover:bg-gray-50">
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          Actualiser
        </button>
      </div>

      {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{error}</div>}

      <div className="flex gap-1 sm:gap-2 mb-6 border-b border-gray-200">
        {[
          { id: 'countries', label: 'Pays', Icon: Globe },
          { id: 'cities', label: 'Villes', Icon: MapPin },
          { id: 'cuisine', label: 'Types de cuisine', Icon: UtensilsCrossed },
          { id: 'dish-categories', label: 'Catégories de plats', Icon: Tag },
        ].map(({ id, label, Icon }) => (
          <button
            key={id}
            type="button"
            onClick={() => setTab(id)}
            className={`flex items-center gap-1.5 sm:gap-2 pl-2 pr-3 sm:px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
              tab === id ? 'border-orange-500 text-orange-600' : 'border-transparent text-gray-500 hover:text-gray-800'
            }`}
          >
            <Icon size={18} />
            {label}
          </button>
        ))}
      </div>

      {tab === 'countries' && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="min-w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                <th className="px-4 py-3">Code</th>
                <th className="px-4 py-3">Nom</th>
                <th className="px-4 py-3">Indicatif</th>
                <th className="px-4 py-3">Ordre</th>
                <th className="px-4 py-3">Actif</th>
                <th className="px-4 py-3 w-28" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {countries.map((c) => (
                <CountryRow key={c.id} c={c} onSaved={loadCountries} onError={setError} editing={editingCountry === c.id} setEditing={setEditingCountry} />
              ))}
              <NewCountryRow onCreated={loadCountries} onError={setError} />
            </tbody>
          </table>
        </div>
      )}

      {tab === 'cities' && (
        <div>
          <div className="mb-4 flex items-center gap-3">
            <label className="text-sm text-gray-600">Filtrer par pays :</label>
            <select
              className={input + ' max-w-xs'}
              value={filterCountryId}
              onChange={(e) => setFilterCountryId(e.target.value)}
            >
              <option value="">Tous</option>
              {countries.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name} ({c.code})
                </option>
              ))}
            </select>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <table className="min-w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  <th className="px-4 py-3">Pays</th>
                  <th className="px-4 py-3">Ville</th>
                  <th className="px-4 py-3">Ordre</th>
                  <th className="px-4 py-3">Actif</th>
                  <th className="px-4 py-3 w-24" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {cities.map((city) => (
                  <CityRow key={city.id} city={city} countries={countries} onSaved={loadCities} onError={setError} />
                ))}
                <NewCityRow countries={countries} onCreated={loadCities} onError={setError} />
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'cuisine' && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="min-w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                <th className="px-4 py-3">Nom</th>
                <th className="px-4 py-3">Ordre</th>
                <th className="px-4 py-3">Actif</th>
                <th className="px-4 py-3 w-24" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {cuisines.map((x) => (
                <CuisineRow key={x.id} row={x} onSaved={loadCuisines} onError={setError} />
              ))}
              <NewCuisineRow onCreated={loadCuisines} onError={setError} />
            </tbody>
          </table>
        </div>
      )}

      {tab === 'dish-categories' && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="min-w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                <th className="px-4 py-3">Nom</th>
                <th className="px-4 py-3 w-24" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {dishCategories.map((x) => (
                <DishCategoryRow key={x.id} row={x} onSaved={loadDishCategories} onError={setError} />
              ))}
              <NewDishCategoryRow onCreated={loadDishCategories} onError={setError} />
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function CountryRow({ c, onSaved, onError, editing, setEditing }) {
  const [code, setCode] = useState(c.code);
  const [name, setName] = useState(c.name);
  const [dial, setDial] = useState(c.dial_code || '+33');
  const [sort, setSort] = useState(c.sort_order ?? 0);
  const [active, setActive] = useState(c.is_active !== false);

  useEffect(() => {
    setCode(c.code);
    setName(c.name);
    setDial(c.dial_code || '+33');
    setSort(c.sort_order ?? 0);
    setActive(c.is_active !== false);
  }, [c]);

  const save = async () => {
    try {
      await updateAdminReferenceCountry(c.id, {
        code: code.trim().toUpperCase(),
        name: name.trim(),
        dial_code: dial.trim(),
        sort_order: Number(sort),
        is_active: active,
      });
      setEditing(null);
      await onSaved();
    } catch (e) {
      onError(e.message);
    }
  };

  return (
    <tr className="hover:bg-gray-50">
      <td className="px-4 py-3">
        <input className={input} value={code} onChange={(e) => setCode(e.target.value)} disabled={!editing} />
      </td>
      <td className="px-4 py-3">
        <input className={input} value={name} onChange={(e) => setName(e.target.value)} disabled={!editing} />
      </td>
      <td className="px-4 py-3">
        <input className={input} value={dial} onChange={(e) => setDial(e.target.value)} disabled={!editing} />
      </td>
      <td className="px-4 py-3">
        <input type="number" className={input + ' w-20'} value={sort} onChange={(e) => setSort(e.target.value)} disabled={!editing} />
      </td>
      <td className="px-4 py-3">
        <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} disabled={!editing} />
      </td>
      <td className="px-4 py-3 text-right space-x-1">
        {editing ? (
          <button type="button" className="text-xs px-2 py-1 rounded bg-green-600 text-white" onClick={save}>
            OK
          </button>
        ) : (
          <button type="button" className="p-1 rounded hover:bg-gray-100" onClick={() => setEditing(c.id)}>
            <Pencil size={16} />
          </button>
        )}
        <button
          type="button"
          className="p-1 rounded hover:bg-red-50 text-red-600"
          onClick={async () => {
            if (!window.confirm('Supprimer ce pays et toutes ses villes ?')) return;
            try {
              await deleteAdminReferenceCountry(c.id);
              await onSaved();
            } catch (e) {
              onError(e.message);
            }
          }}
        >
          <Trash2 size={16} />
        </button>
      </td>
    </tr>
  );
}

function NewCountryRow({ onCreated, onError }) {
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [dial, setDial] = useState('+33');
  const [sort, setSort] = useState(0);
  const [active, setActive] = useState(true);

  const add = async () => {
    if (!code.trim() || !name.trim()) {
      onError('Code et nom obligatoires');
      return;
    }
    try {
      await createAdminReferenceCountry({
        code: code.trim().toUpperCase(),
        name: name.trim(),
        dial_code: dial.trim() || '+33',
        sort_order: Number(sort) || 0,
        is_active: active,
      });
      setCode('');
      setName('');
      setDial('+33');
      setSort(0);
      setActive(true);
      await onCreated();
    } catch (e) {
      onError(e.message);
    }
  };

  return (
    <tr className="bg-gray-50/80">
      <td className="px-4 py-3">
        <input className={input} placeholder="ex. FR" value={code} onChange={(e) => setCode(e.target.value)} />
      </td>
      <td className="px-4 py-3">
        <input className={input} placeholder="Nom du pays" value={name} onChange={(e) => setName(e.target.value)} />
      </td>
      <td className="px-4 py-3">
        <input className={input} value={dial} onChange={(e) => setDial(e.target.value)} />
      </td>
      <td className="px-4 py-3">
        <input type="number" className={input + ' w-20'} value={sort} onChange={(e) => setSort(e.target.value)} />
      </td>
      <td className="px-4 py-3">
        <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
      </td>
      <td className="px-4 py-3 text-right">
        <button type="button" onClick={add} className="inline-flex items-center gap-1 text-sm px-3 py-1.5 rounded-lg bg-gray-900 text-white">
          <Plus size={16} />
          Ajouter
        </button>
      </td>
    </tr>
  );
}

function CityRow({ city, countries, onSaved, onError }) {
  const [name, setName] = useState(city.name);
  const [sort, setSort] = useState(city.sort_order ?? 0);
  const [active, setActive] = useState(city.is_active !== false);
  const [edit, setEdit] = useState(false);
  const countryName = countries.find((x) => x.id === city.country_id)?.name || city.country_id;

  const save = async () => {
    try {
      await updateAdminReferenceCity(city.id, { name: name.trim(), sort_order: Number(sort), is_active: active });
      setEdit(false);
      await onSaved();
    } catch (e) {
      onError(e.message);
    }
  };

  return (
    <tr className="hover:bg-gray-50">
      <td className="px-4 py-3 text-gray-600">{countryName}</td>
      <td className="px-4 py-3">
        <input className={input} value={name} onChange={(e) => setName(e.target.value)} disabled={!edit} />
      </td>
      <td className="px-4 py-3">
        <input type="number" className={input + ' w-20'} value={sort} onChange={(e) => setSort(e.target.value)} disabled={!edit} />
      </td>
      <td className="px-4 py-3">
        <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} disabled={!edit} />
      </td>
      <td className="px-4 py-3 text-right space-x-1">
        {edit ? (
          <button type="button" className="text-xs px-2 py-1 rounded bg-green-600 text-white" onClick={save}>
            OK
          </button>
        ) : (
          <button type="button" className="p-1 rounded hover:bg-gray-100" onClick={() => setEdit(true)}>
            <Pencil size={16} />
          </button>
        )}
        <button
          type="button"
          className="p-1 rounded hover:bg-red-50 text-red-600"
          onClick={async () => {
            if (!window.confirm('Supprimer cette ville ?')) return;
            try {
              await deleteAdminReferenceCity(city.id);
              await onSaved();
            } catch (e) {
              onError(e.message);
            }
          }}
        >
          <Trash2 size={16} />
        </button>
      </td>
    </tr>
  );
}

function NewCityRow({ countries, onCreated, onError }) {
  const [countryId, setCountryId] = useState(countries[0]?.id || '');
  const [name, setName] = useState('');
  const [sort, setSort] = useState(0);

  useEffect(() => {
    if (countries.length && !countryId) setCountryId(countries[0].id);
  }, [countries, countryId]);

  const add = async () => {
    if (!countryId || !name.trim()) {
      onError('Choisissez un pays et un nom de ville');
      return;
    }
    try {
      await createAdminReferenceCity({
        country_id: Number(countryId),
        name: name.trim(),
        sort_order: Number(sort) || 0,
        is_active: true,
      });
      setName('');
      setSort(0);
      await onCreated();
    } catch (e) {
      onError(e.message);
    }
  };

  return (
    <tr className="bg-gray-50/80">
      <td className="px-4 py-3">
        <select className={input} value={countryId} onChange={(e) => setCountryId(e.target.value)}>
          {countries.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
      </td>
      <td className="px-4 py-3">
        <input className={input} placeholder="Nom de la ville" value={name} onChange={(e) => setName(e.target.value)} />
      </td>
      <td className="px-4 py-3">
        <input type="number" className={input + ' w-20'} value={sort} onChange={(e) => setSort(e.target.value)} />
      </td>
      <td className="px-4 py-3">—</td>
      <td className="px-4 py-3 text-right">
        <button type="button" onClick={add} className="inline-flex items-center gap-1 text-sm px-3 py-1.5 rounded-lg bg-gray-900 text-white">
          <Plus size={16} />
          Ajouter
        </button>
      </td>
    </tr>
  );
}

function CuisineRow({ row, onSaved, onError }) {
  const [name, setName] = useState(row.name);
  const [sort, setSort] = useState(row.sort_order ?? 0);
  const [active, setActive] = useState(row.is_active !== false);
  const [edit, setEdit] = useState(false);

  useEffect(() => {
    setName(row.name);
    setSort(row.sort_order ?? 0);
    setActive(row.is_active !== false);
  }, [row]);

  const save = async () => {
    try {
      await updateAdminReferenceCuisineType(row.id, { name: name.trim(), sort_order: Number(sort), is_active: active });
      setEdit(false);
      await onSaved();
    } catch (e) {
      onError(e.message);
    }
  };

  return (
    <tr className="hover:bg-gray-50">
      <td className="px-4 py-3">
        <input className={input} value={name} onChange={(e) => setName(e.target.value)} disabled={!edit} />
      </td>
      <td className="px-4 py-3">
        <input type="number" className={input + ' w-20'} value={sort} onChange={(e) => setSort(e.target.value)} disabled={!edit} />
      </td>
      <td className="px-4 py-3">
        <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} disabled={!edit} />
      </td>
      <td className="px-4 py-3 text-right space-x-1">
        {edit ? (
          <button type="button" className="text-xs px-2 py-1 rounded bg-green-600 text-white" onClick={save}>
            OK
          </button>
        ) : (
          <button type="button" className="p-1 rounded hover:bg-gray-100" onClick={() => setEdit(true)}>
            <Pencil size={16} />
          </button>
        )}
        <button
          type="button"
          className="p-1 rounded hover:bg-red-50 text-red-600"
          onClick={async () => {
            if (!window.confirm('Supprimer ce type ?')) return;
            try {
              await deleteAdminReferenceCuisineType(row.id);
              await onSaved();
            } catch (e) {
              onError(e.message);
            }
          }}
        >
          <Trash2 size={16} />
        </button>
      </td>
    </tr>
  );
}

function NewCuisineRow({ onCreated, onError }) {
  const [name, setName] = useState('');
  const [sort, setSort] = useState(0);

  const add = async () => {
    if (!name.trim()) {
      onError('Nom obligatoire');
      return;
    }
    try {
      await createAdminReferenceCuisineType({ name: name.trim(), sort_order: Number(sort) || 0, is_active: true });
      setName('');
      setSort(0);
      await onCreated();
    } catch (e) {
      onError(e.message);
    }
  };

  return (
    <tr className="bg-gray-50/80">
      <td className="px-4 py-3">
        <input className={input} placeholder="ex. Méditerranéenne" value={name} onChange={(e) => setName(e.target.value)} />
      </td>
      <td className="px-4 py-3">
        <input type="number" className={input + ' w-20'} value={sort} onChange={(e) => setSort(e.target.value)} />
      </td>
      <td className="px-4 py-3">✓</td>
      <td className="px-4 py-3 text-right">
        <button type="button" onClick={add} className="inline-flex items-center gap-1 text-sm px-3 py-1.5 rounded-lg bg-gray-900 text-white">
          <Plus size={16} />
          Ajouter
        </button>
      </td>
    </tr>
  );
}

function DishCategoryRow({ row, onSaved, onError }) {
  const [name, setName] = useState(row.name);
  const [edit, setEdit] = useState(false);

  useEffect(() => {
    setName(row.name);
  }, [row]);

  const save = async () => {
    if (!name.trim()) {
      onError('Nom obligatoire');
      return;
    }
    try {
      await updateAdminDishCategory(row.id, { name: name.trim() });
      setEdit(false);
      await onSaved();
    } catch (e) {
      onError(e.message);
    }
  };

  return (
    <tr className="hover:bg-gray-50">
      <td className="px-4 py-3">
        <input className={input} value={name} onChange={(e) => setName(e.target.value)} disabled={!edit} />
      </td>
      <td className="px-4 py-3 text-right space-x-1">
        {edit ? (
          <button type="button" className="text-xs px-2 py-1 rounded bg-green-600 text-white" onClick={save}>
            OK
          </button>
        ) : (
          <button type="button" className="p-1 rounded hover:bg-gray-100" onClick={() => setEdit(true)}>
            <Pencil size={16} />
          </button>
        )}
        <button
          type="button"
          className="p-1 rounded hover:bg-red-50 text-red-600"
          onClick={async () => {
            if (!window.confirm('Supprimer cette catégorie ?')) return;
            try {
              await deleteAdminDishCategory(row.id);
              await onSaved();
            } catch (e) {
              onError(e.message);
            }
          }}
        >
          <Trash2 size={16} />
        </button>
      </td>
    </tr>
  );
}

function NewDishCategoryRow({ onCreated, onError }) {
  const [name, setName] = useState('');

  const add = async () => {
    if (!name.trim()) {
      onError('Nom obligatoire');
      return;
    }
    try {
      await createAdminDishCategory({ name: name.trim() });
      setName('');
      await onCreated();
    } catch (e) {
      onError(e.message);
    }
  };

  return (
    <tr className="bg-gray-50/80">
      <td className="px-4 py-3">
        <input className={input} placeholder="ex. Petit-déjeuner" value={name} onChange={(e) => setName(e.target.value)} />
      </td>
      <td className="px-4 py-3 text-right">
        <button type="button" onClick={add} className="inline-flex items-center gap-1 text-sm px-3 py-1.5 rounded-lg bg-gray-900 text-white">
          <Plus size={16} />
          Ajouter
        </button>
      </td>
    </tr>
  );
}
